/**
 * Filet de sécurité : déclare des stubs pour tous les gestionnaires
 * inline (`onclick`, `onchange`, `oninput`…) présents dans le markup.
 * Les écrans encore dépourvus de contrôleur restent navigables sans
 * ReferenceError. Un bouton visible doit être branché par son contrôleur :
 * le stub signale alors une action non branchée, jamais une fonctionnalité
 * « à venir ».
 *
 * Les stubs ne sont posés que si aucune fonction du même nom n'existe déjà, et
 * les contrôleurs de vue (exécutés à la navigation) remplacent ces stubs par
 * les implémentations réelles branchées sur les services métier.
 */
import { ToastService } from '@presentation/components/Toast';

/** Gestionnaires « action » : un toast informatif est plus clair qu'un silence. */
const ACTION_PREFIXES = [
  'open',
  'export',
  'gener',
  'save',
  'enregistrer',
  'imprimer',
  'print',
  'activer',
  'simuler',
  'inventaire',
  'purge',
  'seed',
  'archiver',
  'rapprocher',
  'valider',
  'envoyer',
  'handleFileUpload',
];

const HANDLER_NAMES = [
  'activerLicence',
  'addLigneManuelle',
  'archiverStcDepuisConformite',
  'calculerPaie',
  'changePeriode',
  'cloturerPeriodeCompta',
  'deverrouillerPeriodeCompta',
  'enregistrerDPO',
  'enregistrerEcritureManuelle',
  'enregistrerRapportGestion',
  'enregistrerRI',
  'envoyerBulletins',
  'exportAudit',
  'exportBackup',
  'exportBulletinsCSV',
  'exportCNAMGS',
  'exportCNSS',
  'exportComptabiliteCSV',
  'exportComptabiliteOHADA',
  'exportDAS2',
  'exportDeclarationTVA',
  'exportDGI',
  'exportEtvaXml',
  'downloadSalarieCsvTemplate',
  'downloadSaisieCsvTemplate',
  'exporterSaisie',
  'exportFEC',
  'exportSalariesCSV',
  'exportSyntheseCSV',
  'filterUsers',
  'genererAttestation',
  'genererDAS',
  'genererEcrituresPaie',
  'genererISAUI',
  'genererISPUI',
  'genererMD2DUI',
  'genererProvisionsExercice',
  'genererReserveLegaleUI',
  'gotoCurrentMonth',
  'gotoOffsetMonth',
  'handleFileUpload',
  'importBackup',
  'importerSaisie',
  'imprimerRapportGestion',
  'imprimerRI',
  'inventaireAnnuel',
  'navigate',
  'openAGModal',
  'openCdcModal',
  'openCongeModal',
  'openCongeSelfModal',
  'openConventionModal',
  'openDemandeRGPDModal',
  'openDistributionModal',
  'openDtsModal',
  'openEvaluationModal',
  'openExerciceModal',
  'openFactureModal',
  'openImmoModal',
  'openImmoAnModal',
  'openImmoComptableModal',
  'passerDotationsImmo',
  'openVehiculeModal',
  'openFraisModal',
  'openImportSalaries',
  'openOffreModal',
  'openSalarieModal',
  'openSanctionModal',
  'openStockModal',
  'openSuspensionModal',
  'openTiersModal',
  'openTraitementModal',
  'openUserModal',
  'printAllBulletins',
  'printSelectedBulletins',
  'purgeDemoERP',
  'rapprocherTous',
  'renderAnaBudget',
  'renderAnaSIG',
  'renderBalance',
  'renderCentreQualite',
  'renderCoffrefort',
  'renderConformite',
  'renderEtatsFinanciers',
  'renderGrandLivre',
  'renderRapportGestion',
  'renderSTCSalarie',
  'resetAll',
  'resetEcritureManuelle',
  'resetPlanComptable',
  'resetRapprochements',
  'resetStcConfig',
  'renderParametres',
  'renewLicenceNow',
  'importLicenceFile',
  'deactivateLicenceDevice',
  'checkAppUpdate',
  'applyAppUpdate',
  'upgradeToLatestRelease',
  'pickUsbUpdate',
  'applyUsbManifestFile',
  'resetTiersInstitutionnels',
  'saveDevises',
  'saveEntreprise',
  'saveModulesSelection',
  'savePlanComptable',
  'appliquerConvention',
  'saveStcConfig',
  'saveTiersInstitutionnels',
  'seedAnalytiqueMultiExercices',
  'seedCentresAnalytiquesDemo',
  'seedDemoData',
  'seedDemoEmployees',
  'seedERPData',
  'seedERPHistorique',
  'selectAllModules',
  'setPatenteForfait',
  'setPeriode',
  'setProfilFiscal',
  'setSystemeComptable',
  'setUsersRoleFilter',
  'simulerIRVM',
  'switchAnaTab',
  'switchTab',
  'toggle13e',
  'toggleAllBulletinsSelection',
  'validerBulletins',
];

export function installGlobalStubs(): void {
  const w = window as unknown as Record<string, unknown>;
  for (const name of HANDLER_NAMES) {
    if (typeof w[name] === 'function') continue; // déjà implémenté (shell/contrôleur)
    const isAction = ACTION_PREFIXES.some((p) => name.startsWith(p));
    w[name] = isAction
      ? () => {
          console.error(`handler_missing:${name}`);
          ToastService.error(`Action « ${name} » non branchée`);
        }
      : () => {
          /* stub silencieux (render/set/filter/toggle…) */
        };
  }
}
